<template lang="pug">
.dashboard-wrapper
  b-row
    b-col
      b-card(no-body, class="pt-2 px-2")
        filters(
          :customFiltersShow="true"
          :locationFilterShow="true"
          :dateRangeFilterShow="true"
          :locationDefaultValue="true"
          :physicalLocationFilterShow="true"
          :areaFilterShow="true"
          @changeFilter="updateFilter"
          @locations="updateLocations"
        )
        
  b-row(v-if="isLoadingMeetings || isLoadingCompliance || isLoadingConfirmations")
    b-col
      b-card(class="py-5")
        loading(
          :active="true" 
          :is-full-page="false"
          :color="colors.primary"
        )

  div(v-else)
    //- Send report button
    div.d-flex.align-items-center.justify-content-end.mb-2
      span.mr-2
        | {{ sendingReportMessage }}
      b-button(variant="primary", :disabled="!!sendingReportMessage", @click="sendReportEmail")
        | {{ $t('message.sendReportToAreaLeader') }}

    //- Leadership Compliance report
    div#report
      b-card(no-body)
        b-card-title(class="p-2 mb-0 text-center")
          | {{ $t('Leadership Compliance') }}

      //- Daily Dialogue
      b-row
        b-col(md="4")
          b-card(no-body)
            b-card-title(class="p-2 mb-0 text-center")
              | {{ $t('Daily Dialogue') }}

      b-row
        b-col(cols="12")
          heat-map-dialogues(
            v-if="dialoguesPerDay.length",
            :dialogues-per-day-data="dialoguesPerDay"
            i18nTitleVariable="Dialogue Reporting Compliance"
            i18nSubTitleVariable="Dialogues reported per day in Habit"
            :compliance="compliance"
            :complianceColor="complianceColor"
            :default-language="default_language"
          )

      //- Mood
      b-row
        b-col(md="4")
          b-card(no-body)
            b-card-title(class="p-2 mb-0 text-center")
              | {{ $t('Team Mood') }}

      b-row
        b-col(sm="3")
          statistic-card-vertical-index(:statistic="happiness_index")
        b-col(sm="3")
          statistic-card-vertical-happy(:statistic="happinessHappy")
        b-col(sm="3")
          statistic-card-vertical-neutral(:statistic="happinessNeutral")
        b-col(sm="3")
          statistic-card-vertical-sad(:statistic="happinessSad")

      b-row
        b-col(cols="12")
          bar-chart-happiness(
            v-if="happinessPerDay.labels && happinessPerDay.labels.length",
            :happiness-data="happinessPerDay"
          )
      
      //- Process Confirmation
      b-row
        b-col(md="4")
          b-card(no-body)
            b-card-title(class="p-2 mb-0 text-center")
              | {{ $t('Process Confirmation') }}
      
      b-row
        //- Compliance
        b-col(lg="6")
          support-tracker-fixed(v-if="compliance_calculation_by_role", :dataForCharts="dataForCharts")
          support-tracker(v-else, :tracker-data="trackerData")

          b-card
            b-table-simple(hover, small, caption-top, responsive)
              b-thead(head-variant="light")
                b-tr
                  th {{ $t('message.tableHeader.supervisor') }}
                  th {{ $t('message.tableHeader.observed') }}
                b-tr(v-for="(supervisor, index) of supervisors", :key="index")
                  b-td {{ supervisor.supervisor }}
                  b-td {{ supervisor.observed }}
      
        //- Adherence
        b-col(lg="6")
          bar-chart-worker-bottom(
            v-if="workerBottomAdherence.labels && workerBottomAdherence.labels.length",
            :worker-bottom-data="workerBottomAdherence"
            i18nTitleVariable="Behaviour Adherence Priorities"
          )
</template>

<script>
import { LOGO_CORREO } from '@/libs/emailAssets'
import { onMounted, ref, computed } from "@vue/composition-api/dist/vue-composition-api"
import { BTable, BTableSimple, BThead, BTr, BTd, BCard, BCardTitle } from "bootstrap-vue"
import HeatMapDialogues from '@/views/habit/happiness/charts/ApexHeatMapDialogues.vue'
import StatisticCardVerticalHappy from '@/views/habit/measureOfHappiness/charts/StatisticCardVerticalHappy.vue'
import StatisticCardVerticalNeutral from '@/views/habit/measureOfHappiness/charts/StatisticCardVerticalNeutral.vue'
import StatisticCardVerticalSad from '@/views/habit/measureOfHappiness/charts/StatisticCardVerticalSad.vue'
import StatisticCardVerticalIndex from '@/views/habit/measureOfHappiness/charts/StatisticCardVerticalIndex.vue'
import BarChartHappiness from '@/views/habit/measureOfHappiness/charts/ChartjsBarChartHappiness.vue'
import SupportTracker from "@/views/habit/confirmation/dashboard/charts/CardAnalyticSupportTracker.vue"
import SupportTrackerFixed from "@/views/habit/confirmation/dashboard/charts/CardAnalyticSupportTrackerFixed.vue"
import BarChartWorkerBottom from '@/views/habit/adherence/processes/charts/ChartjsHorizontalBarChartWorkerBottom.vue'
import Filters from "@/views/organization/Filters.vue"
import i18n from '@/libs/i18n'
import { useRouter } from '@core/utils/utils'
import Loading from 'vue-loading-overlay'
import 'vue-loading-overlay/dist/vue-loading.css'
import store from "@/store"
import useCommon from '@/views/organization/useCommon'
import useCommonDashboards from '@/views/habit/useCommonDashboards'
import { colors } from '@/constants'
import { useLambda } from '@/views/habit/awsLambda'
import { oid, date } from '@/helpers/mongoHelpers'
import endpoints from '@/libs/endpoints'
import html2canvas from 'html2canvas'
import useNotifications from '@/composables/useNotifications'
import { get } from 'idb-keyval';
import useMongoDBFunctions from '@/views/habit/useMongoDBFunctions'

export default {
  components: {
    BTable,
    BTableSimple,
    BThead,
    BTr,
    BTd,
    BCard,
    BCardTitle,
    Filters,
    Loading,
    HeatMapDialogues,
    StatisticCardVerticalHappy,
    StatisticCardVerticalNeutral,
    StatisticCardVerticalSad,
    StatisticCardVerticalIndex,
    BarChartHappiness,
    SupportTracker,
    SupportTrackerFixed,
    BarChartWorkerBottom,
  },
  setup() {
    const { handleError, getPhysicalLocationsForDropDown, physical_locations } = useCommon()
    const { getItems, getItemsAggregate } = useLambda()
    const { confirmationsForWorkers } = useMongoDBFunctions()
    const { sendEmail } = useLambda()
    const { showSuccessMessage, showErrorMessage } = useNotifications()
    const userData = store.state?.userStore?.userData
    const userRole = userData.role
    const userId = userData.worker_id != null ? userData.worker_id.$oid : null
    const clientId = userData.role !== "admin" ? userData.client.$oid : null
    const userLocations = userData.locations?.length ? userData.locations : null
    const workerBottomAdherence = ref({})
    const locationFilter = ref(userLocations ? userLocations.map(e => e.value) : [])
    const physicalLocationFilter = ref([])
    const { default_language, compliance_calculation_by_role } = JSON.parse(localStorage.getItem('clientData') || '{}')
    const now = new Date()
    const currentMonth = now.getMonth()
    const currentYear = now.getFullYear()
    const lastDayOfMonthDate = new Date(currentYear, currentMonth + 1, 0)
    const lastDayOfMonthNumber = lastDayOfMonthDate.getDate()
    const dateRangeFilter = ref(`01-${currentMonth + 1}-${currentYear} to ${lastDayOfMonthNumber}-${currentMonth + 1}-${currentYear}`)
    const { router } = useRouter()
    const { processAdherence, getDatesFromRange, formatDateUTC, happinessIndex, dateDiffInDays } = useCommonDashboards()
    const isLoadingMeetings = ref(true)
    const isLoadingCompliance = ref(true)
    const isLoadingConfirmations = ref(true)
    const compliance = ref(0)
    const complianceColor = ref('text-warning')
    const locations = ref([])
    get('locations').then(response => {
      if (response) locations.value = response
      else if (localStorage.locations) locations.value = JSON.parse(localStorage.locations)
    })
    const allLocationsData = ref([])
    const selectedLocationsData = ref([])
    const dialoguesPerDay = ref([])
    const happiness_index = ref(0)
    const happinessPerDay = ref({})
    const happinessHappy = ref(0)
    const happinessNeutral = ref(0)
    const happinessSad = ref(0)
    const trackerData = ref({})
    const dataForCharts = ref({})
    const supervisors = ref([])
    const sendingReportMessage = ref('')

    // Daily Dialogues and Mood
    async function listMeetings() {
      isLoadingMeetings.value = true
      const [startFilter, endFilter] = getDatesFromRange(dateRangeFilter.value)

      try {
        const initialQuery = {
          client_id: oid(clientId),
          deleted: { $ne: true },
          date: { $gte: date(startFilter), $lt: date(endFilter) },
        }

        const finalQuery = {}

        if (locationFilter.value.length > 0) {
          finalQuery['location._id'] = { $in: locationFilter.value }
        }

        if (physicalLocationFilter.value.length > 0) {
          finalQuery['physical_location._id'] = { $in: physicalLocationFilter.value }
        }

        const pipeline = [
          { $match: initialQuery },
          {
            $lookup: {
              from: 'location',
              localField: 'location',
              foreignField: '_id',
              pipeline: [
                { $project: { location: 1, dailyDialoguesCount: 1, physical_location: 1 } },
                {
                  $lookup: {
                    from: 'physical_location',
                    localField: 'physical_location',
                    foreignField: '_id',
                    pipeline: [ { $project: { name: 1 } }, { $addFields: { _id: { $toString: "$_id" } } }],
                    as: 'physical_location'
                  }
                },
                { $addFields: { _id: { $toString: "$_id" }, physical_location: { $arrayElemAt: ["$physical_location", 0] } } }
              ],
              as: 'location'
            }
          },
          {
            $lookup: {
              from: 'physical_location',
              localField: 'physical_location',
              foreignField: '_id',
              pipeline: [ { $project: { name: 1 } }, { $addFields: { _id: { $toString: "$_id" } } }],
              as: 'physical_location'
            }
          },
          { $match: finalQuery },
          { $addFields: { _id: { $toString: "$_id" }, location: { $arrayElemAt: ["$location", 0] }, physical_location: { $arrayElemAt: ["$physical_location", 0] } } },
          { $sort: { date: 1 } },
        ]
      
        const items = await getItemsAggregate({ collection: 'meeting', pipeline })
        
        await parseMeetingData(items, startFilter, endFilter)
      } catch (error) {
        console.log(error);
        handleError({ error, defaultMessage: i18n.t('message.err_daily_dialogues_list') })
      } finally {
        isLoadingMeetings.value = false
      }
    }

    async function parseMeetingData(items, startFilter, endFilter) {
      let dailyDialoguesData = { total: 0 }
      let dailyHappinessData = {}
      let dynamicDate = new Date(startFilter)

      // Generate all dates in the range using UTC to avoid timezone issues
      while (dynamicDate < endFilter) {
        const dateToAdd = formatDateUTC(dynamicDate, default_language)
        dailyDialoguesData[dateToAdd] = { dialogues: 0, images: 0, mood: 0, improvements: 0, recognitions: 0 }
        dailyHappinessData[dateToAdd] = { happy: 0, neutral: 0, sad: 0 }
        dynamicDate.setUTCDate(dynamicDate.getUTCDate() + 1)
      }

      let locationDataDaily = {}
      let happiness_data = { happy: 0, neutral: 0, sad: 0 }

      for (const meeting of items) {
        const date = new Date(meeting.date)
        const dateString = formatDateUTC(date, default_language)
        const location = meeting.location?.location
        const locationWithPhysicalLocation = `${meeting.location?.physical_location?.name ? `${meeting.location?.physical_location.name} - ` : ''}${meeting.location?.location}`
        const locationId = meeting.location?._id
        const mood = meeting.happy + meeting.neutral + meeting.sad
        const imageKey = meeting.imageKey
        const improvements = meeting.improvements
        const recognitions = meeting.recognitions

        // Heat map
        if (locationWithPhysicalLocation) {
          if (!locationDataDaily.hasOwnProperty(locationWithPhysicalLocation)) {
            locationDataDaily[locationWithPhysicalLocation] = JSON.parse(JSON.stringify(dailyDialoguesData))
            locationDataDaily[locationWithPhysicalLocation].id = locationId
            locationDataDaily[locationWithPhysicalLocation].dailyDialoguesCount = meeting.location?.dailyDialoguesCount ?? 1
          }
          locationDataDaily[locationWithPhysicalLocation][dateString].dialogues++
          locationDataDaily[locationWithPhysicalLocation][dateString].images += imageKey ? 1 : 0
          locationDataDaily[locationWithPhysicalLocation][dateString].mood += mood
          locationDataDaily[locationWithPhysicalLocation][dateString].improvements += improvements?.length || 0
          locationDataDaily[locationWithPhysicalLocation][dateString].recognitions += recognitions?.length || 0
          locationDataDaily[locationWithPhysicalLocation].total++
        }

        // Statistic cards
        happiness_data.happy += meeting.happy
        happiness_data.neutral += meeting.neutral
        happiness_data.sad += meeting.sad

        // Bar chart
        dailyHappinessData[dateString].happy += meeting.happy
        dailyHappinessData[dateString].neutral += meeting.neutral
        dailyHappinessData[dateString].sad += meeting.sad
      }

      // Compliance
      let expectedDialoguesTotal = 0
      let dialoguesToComputeTotal = 0
      
      if (!allLocationsData.value?.length) await getLocationsData()

      // selectedLocationsData.value = locationFilter.value?.length
      //   ? allLocationsData.value?.filter(loc => locationFilter.value?.includes(loc._id?.toString()))
      //   : allLocationsData.value?.filter(loc => locations.value?.map(l => l.value)?.includes(loc._id?.toString()))

      const selectedLocationsIds = []

      for (const location in locationDataDaily) {
        selectedLocationsIds.push(locationDataDaily[location].id)
      }

      selectedLocationsData.value = allLocationsData.value?.filter(loc => selectedLocationsIds.includes(loc._id?.toString()))

      // Compliance calculation by location
      for (const loc of selectedLocationsData.value) {
        const dailyDialoguesCount = loc.dailyDialoguesCount ?? 1
        // const dailyDialoguesCount = loc.dailyDialoguesCount || 1

        if (dailyDialoguesCount === 0) continue

        loc.dailyDialogueDays = loc.dailyDialogueDays || [1, 2, 3, 4, 5, 6, 0] // Default to all days of the week

        // Calculation of days considered
        let qDays = 0
        if (endFilter < now) qDays = dateDiffInDays(startFilter, endFilter, loc.dailyDialogueDays)
        else if (startFilter < now) qDays = dateDiffInDays(startFilter, now, loc.dailyDialogueDays)
  
        loc.qDays = qDays
        loc.expectedDialogues = qDays * dailyDialoguesCount

        // Calculation of actual dialogues completed (don't consider the extra dialogues made if they exceeded dailyDialoguesCount, since the compliance is calculated based on the expected dialogues)
        let dialoguesToCompute = 0
        const locWithPhysicalLocation = `${loc.physical_location?.name ? `${loc.physical_location.name} - ` : ''}${loc.location}`
        
        if (locationDataDaily[locWithPhysicalLocation]) {
          for (const date in locationDataDaily[locWithPhysicalLocation]) {
            dialoguesToCompute += Math.min(locationDataDaily[locWithPhysicalLocation][date].dialogues || 0, dailyDialoguesCount)
          }
        }

        loc.compliance = loc.expectedDialogues === 0
          ? "-"
          : `${Math.min(100, Math.round((dialoguesToCompute / loc.expectedDialogues) * 1000) / 10)}%`;

        // Global accumulators
        expectedDialoguesTotal += loc.expectedDialogues
        dialoguesToComputeTotal += dialoguesToCompute
      }

      // Calculation of overall adherence
      const complianceNumericValue = expectedDialoguesTotal === 0
        ? 0
        : Math.min(100, Math.round((dialoguesToComputeTotal / expectedDialoguesTotal) * 1000) / 10)

      compliance.value = expectedDialoguesTotal === 0
        ? "-"
        : `${complianceNumericValue}%`

      complianceColor.value = complianceNumericValue > 90
        ? 'text-success'
        : complianceNumericValue >= 60
          ? 'text-warning'
          : 'text-danger'

      // Heat map
      const heatMapData = []

      for (const location in locationDataDaily) {
        const heatMapLocationData = {
          name: location,
          data: [],
          total: locationDataDaily[location].total,
          locationId: locationDataDaily[location].id,
          dailyDialoguesCount: locationDataDaily[location].dailyDialoguesCount
        }

        delete locationDataDaily[location].total
        delete locationDataDaily[location].id
        delete locationDataDaily[location].dailyDialoguesCount

        for (const date in locationDataDaily[location]) {
          heatMapLocationData.data.push({
            x: date,
            y: locationDataDaily[location][date].dialogues,
            images: locationDataDaily[location][date].images,
            mood: locationDataDaily[location][date].mood,
            improvements: locationDataDaily[location][date].improvements,
            recognitions: locationDataDaily[location][date].recognitions
          })
        }

        heatMapData.push(heatMapLocationData)
      }

      // Sort heat map data
      heatMapData.sort(function (a, b) {
        if (b.total === a.total) {
          if (a.name > b.name) return -1
          if (a.name < b.name) return 1
          return 0
        }
        return a.total - b.total
      })

      dialoguesPerDay.value = heatMapData

      // Statistic cards
      happinessHappy.value = happiness_data.happy
      happinessNeutral.value = happiness_data.neutral
      happinessSad.value = happiness_data.sad
      happiness_index.value = (!happiness_data.happy && !happiness_data.neutral && !happiness_data.sad)
        ? "-"
        : `${happinessIndex(happiness_data.happy, happiness_data.neutral, happiness_data.sad)}%`

      // Bar chart
      let dailyHappinessLabels = Object.keys(dailyHappinessData)
      let dailyHappinessValues = []

      for (const date in dailyHappinessData){
        dailyHappinessValues.push([dailyHappinessData[date].happy, dailyHappinessData[date].neutral, dailyHappinessData[date].sad])
      }

      happinessPerDay.value = {
        labels: dailyHappinessLabels,
        data: dailyHappinessValues,
      };
    }

    const getLocationsData = async () => {
      const query = { client_id: oid(clientId), deleted: { $ne: true } }
      try {
        const pipeline = [
          { $match: query },
          {
            $lookup: {
              from: 'physical_location',
              localField: 'physical_location',
              foreignField: '_id',
              pipeline: [  { $project: { name: 1 } },  { $addFields: { _id: { $toString: "$_id" } } } ],
              as: 'physical_location',
            },
          },
          { $addFields: { _id: { $toString: "$_id" }, physical_location: { $arrayElemAt: ["$physical_location", 0] } } },
        ]
      
        allLocationsData.value = await getItemsAggregate({ collection: 'location', pipeline })
      } catch (error) {
        console.log(error)
      }
    }

    // Compliance Default
    async function listEvents() {
      isLoadingCompliance.value = true
      const [startFilter, endFilter] = getDatesFromRange(dateRangeFilter.value)

      try {
        const initialQuery = {
          client_id: oid(clientId),
          'extendedProps.calendar': { $in: ["Pendientes", "Realizadas", "Vencidas"] },
          start: { $gte: date(startFilter), $lt: date(endFilter) },
          isBehaviourEvaluation: { $ne: true }
        }

        const finalQuery = {
          $or: [
            { "attendee.deleted": { $ne: true } },
            { "attendee.deleted": true, 'extendedProps.calendar': "Realizadas" },
            { attendee: null }
          ]
        }

        if (locationFilter.value.length > 0) {
          finalQuery.$or[0]['attendee.locations._id'] = { $in: locationFilter.value }
          finalQuery.$or[1]['attendee.locations._id'] = { $in: locationFilter.value }
          finalQuery.$or[2]['organizer.locations._id'] = { $in: locationFilter.value }
        }

        if (physicalLocationFilter.value.length > 0) {
          finalQuery.$or[0]['attendee.physical_locations._id'] = { $in: physicalLocationFilter.value }
          finalQuery.$or[1]['attendee.physical_locations._id'] = { $in: physicalLocationFilter.value }
          finalQuery.$or[2]['organizer.physical_locations._id'] = { $in: physicalLocationFilter.value }
        }

        const pipeline = [
          { $match: initialQuery },
          { $lookup: { from: 'worker', localField: 'attendee', foreignField: '_id', as: 'attendee', pipeline: [ { $project: { deleted: 1, supervisors: 1, name: 1, locations: 1, roles: 1, physical_locations: 1 } }, { $addFields: { _id: { $toString: "$_id" } } }, { $lookup: { from: 'worker', localField: 'supervisors', foreignField: '_id', as: 'supervisors', pipeline: [ { $project: { name: 1, locations: 1, physical_locations: 1 } }, { $addFields: { _id: { $toString: "$_id" } } }, { $lookup: { from: 'location', localField: 'locations', foreignField: '_id', as: 'locations', pipeline: [ { $project: { location: 1 } }, { $addFields: { _id: { $toString: "$_id" } } } ] } }, { $lookup: { from: 'physical_location', localField: 'physical_locations', foreignField: '_id',  as: 'physical_locations', pipeline: [ { $project: { name: 1 } }, { $addFields: { _id: { $toString: "$_id" } } } ] } } ] } }, { $lookup: { from: 'location', localField: 'locations', foreignField: '_id', as: 'locations', pipeline: [ { $project: { location: 1, zone: 1, agency: 1 } }, { $addFields: { _id: { $toString: "$_id" } } } ] } }, { $lookup: { from: 'physical_location', localField: 'physical_locations', foreignField: '_id',  as: 'physical_locations', pipeline: [ { $project: { name: 1 } }, { $addFields: { _id: { $toString: "$_id" } } } ] } } ] } },          
          { $lookup: { from: 'worker', localField: 'organizer', foreignField: '_id', as: 'organizer', pipeline: [ { $project: { name: 1, locations: 1, physical_locations: 1 } }, { $addFields: { _id: { $toString: "$_id" } } }, { $lookup: { from: 'location', localField: 'locations', foreignField: '_id', as: 'locations', pipeline: [ { $project: { location: 1, zone: 1, agency: 1 } }, { $addFields: { _id: { $toString: "$_id" } } } ] } }, { $lookup: { from: 'physical_location', localField: 'physical_locations', foreignField: '_id',  as: 'physical_locations', pipeline: [ { $project: { name: 1 } }, { $addFields: { _id: { $toString: "$_id" } } } ] } } ] } },          
          { $addFields: { attendee: { $arrayElemAt: ["$attendee", 0] }, organizer: { $arrayElemAt: ["$organizer", 0] } } },
          { $match: finalQuery },
          { $lookup: { from: 'confirmation', localField: 'confirmation', foreignField: '_id', as: 'confirmation', pipeline: [ { $lookup: { from: 'worker', localField: 'supervisor', foreignField: '_id', as: 'supervisor', pipeline: [ { $project: { name: 1, locations: 1 } }, { $lookup: { from: 'location', localField: 'locations', foreignField: '_id', as: 'locations', pipeline: [ { $project: { location: 1 } } ] } }] } }, { $addFields: { supervisor: { $arrayElemAt: ["$supervisor", 0] } } } ] } },
          { $addFields: { confirmation: { $arrayElemAt: ["$confirmation", 0] } } },
        ]

        const items = await getItemsAggregate({ collection: 'event', pipeline })

        parseSupervisorData(items)
      } catch (error) {
        console.log(error)
        handleError({ error, defaultMessage: i18n.t('message.err_calendar_events_list') })
      } finally {
        isLoadingCompliance.value = false
      }
    }

    function parseSupervisorData(items) {
      let supervisorData = {};
      let _globalCompliance = { Realizadas: 0, Vencidas: 0, Pendientes: 0 };

      for (const element of items) {
        if (element.deleted) continue

        const status = element.extendedProps?.calendar;

        if (status === "Realizadas") _globalCompliance["Realizadas"] += 1;
        else if (status === "Pendientes") _globalCompliance["Pendientes"] += 1;
        else if (status === "Vencidas") _globalCompliance["Vencidas"] += 1;

        // Iterate supervisors
        // If the event has an attendee, iterate over its supervisors
        if (element.attendee) {
          for (const supervisor of element.attendee.supervisors) {    
            const name = supervisor.name
            const supervisorLocations = supervisor.locations?.map(e => e.location).join(" / ")
            updateSupervisorData(supervisorData, name, supervisorLocations, status)
          }
        }

        // Update supervisors 'observed' property for events with confirmations
        if (element.confirmation && element.confirmation.supervisor) {
          const name = element.confirmation.supervisor.name
          const supervisorLocations = element.confirmation.supervisor.locations?.map(e => e.location).join(" / ")
          updateSupervisorData(supervisorData, name, supervisorLocations, "Observadas")
        }
      }

      // Parse supervisor data for Excel download
      let supervisorDataExcel = []

      for (const supervisor in supervisorData) {
        supervisorDataExcel.push({
          supervisor: supervisor,
          location: supervisorData[supervisor].location,
          compliance: getCompliance(supervisorData[supervisor]),
          done: supervisorData[supervisor].Realizadas,
          pending: supervisorData[supervisor].Pendientes,
          overdue: supervisorData[supervisor].Vencidas,
          observed: supervisorData[supervisor].Observadas,
        })
      }

      supervisorDataExcel.sort(function (a, b) {
        if (b.observed !== a.observed) return b.observed - a.observed
        if (b.overdue !== a.overdue) return b.overdue - a.overdue
        if (b.pending !== a.pending) return b.pending - a.pending
        return a.done - b.done
      })

      supervisors.value = supervisorDataExcel;
      
      // Set tracker data
      trackerData.value = {
        title: computed(() => i18n.t("Confirmation Progress")),
        lastDays: ["Acumulado"],
        total:
          _globalCompliance.Pendientes +
          _globalCompliance.Realizadas +
          _globalCompliance.Vencidas,
        done: _globalCompliance.Realizadas,
        pending: _globalCompliance.Pendientes,
        overdue: _globalCompliance.Vencidas,
      }
    }

    function updateSupervisorData(object, name, locations, status) {    
      if (!object[name]) {
        object[name] = {
          location: locations,
          Realizadas: 0,
          Vencidas: 0,
          Pendientes: 0,
          Observadas: 0,
        };
      }

      if (status === "Realizadas") {
        object[name]["Realizadas"] += 1;
      } else if (status === "Pendientes") {
        object[name]["Pendientes"] += 1;
      } else if (status === "Vencidas") {
        object[name]["Vencidas"] += 1;
      } else if (status === "Observadas") {
        object[name]["Observadas"] += 1;
      }
    }

    function getCompliance(supervisor) {
      const total = supervisor.Realizadas + supervisor.Vencidas + supervisor.Pendientes;
      if (!total) return "N/A"
      const ratio = (supervisor.Realizadas / total) * 100;

      return `${ratio.toFixed(1)}%`;
    }

    // Compliance Fixed
    const listWorkers = async () => {
      const [startFilter, endFilter] = getDatesFromRange(dateRangeFilter.value)
      isLoadingCompliance.value = true

      const query = {
        limit: 50,
        total: true,
        offset: 0,
        userId: userId,
        privileges: userRole,
        client_id: clientId,
        date_gte: startFilter,
        date_lt: endFilter,
        locations: locationFilter.value,
        physical_locations: physicalLocationFilter.value,
        roles: [],
        processes: [],
        supervisors: [],
      }

      try {
        const items = await confirmationsForWorkers(query)
        const workersInside = items?.workers || []
        parseWorkersData(workersInside, startFilter, endFilter)
      } catch (error) {
        console.log(error)
        handleError({ error, defaultMessage: i18n.t('message.err_confirmation_list') })
      } finally {
        isLoadingCompliance.value = false
      }
    }

    const parseWorkersData = (workersInside, startFilter, endFilter) => {
      let dataForSupportTracker = {}
      let supportTrackerTotal = 0
      let supportTrackerTotalDone = 0
      let supportTrackerTotalPending = 0
      let supportTrackerTotalMeta = 0

      for(let element of workersInside){
        if (element.rolesData[0]?.numEventsRequired) {
          if (Math.floor(dateDiffInDays(startFilter, endFilter)) <= 31) element.meta = element.rolesData[0].numEventsRequired
          else if (Math.floor(dateDiffInDays(startFilter, endFilter)) <= 62 && Math.floor(dateDiffInDays(startFilter, endFilter)) > 31) element.meta = (element.rolesData[0].numEventsRequired) * 2
          else element.meta = (element.rolesData[0].numEventsRequired) * 3

          if (element.confirmationsCount <= 0){
            element.pending = Math.floor((dateDiffInDays(startFilter, endFilter) * parseInt(element.rolesData[0].numEventsRequired)) / 30)
          } else if (element.confirmationsCount >= Math.floor((dateDiffInDays(startFilter, endFilter) * parseInt(element.rolesData[0].numEventsRequired)) / 30) ){
            element.pending = 0
          } else {
            element.pending = Math.floor((dateDiffInDays(startFilter, endFilter) * parseInt(element.rolesData[0].numEventsRequired)) / 30) - element.confirmationsCount;
          }
        } else {
          element.meta = 0
          element.pending = 0
        }
        
        if (element.rolesData[0]?.numEventsRequired) {
          if (element.pending === 0) {
            element.compliance = Number((element.confirmationsCount / element.meta)*100).toFixed(1)
            if (element.compliance === "0.0") element.compliance = 0.0
            element.compliance = `${element.compliance}%`
          } else if (element.confirmationsCount <= 0) {
            element.compliance = `${0}%`
          } else {
            element.compliance = Number((element.confirmationsCount / element.meta)*100).toFixed(1)
            if (element.compliance === "100.0") element.compliance = 100
            element.compliance = `${element.compliance}%`
          }
        } else {
          if (element.pending === 0) {
            element.compliance = Number((element.confirmationsCount / 1)*100).toFixed(1)
            if (element.compliance === "0.0") element.compliance = 0.0
            element.compliance = `${element.compliance}%`
          } else if (element.confirmationsCount <= 0) {
            element.compliance = `${0}%`
          } else {
            element.compliance = Number((element.confirmationsCount / 1)*100).toFixed(1)
            if (element.compliance === "100.0") element.compliance = 100
            element.compliance = `${element.compliance}%`
          }
        }
        supportTrackerTotalDone = supportTrackerTotalDone + element.confirmationsCount
        supportTrackerTotalPending = supportTrackerTotalPending + element.pending
        supportTrackerTotalMeta = supportTrackerTotalMeta + element.meta
        supportTrackerTotal =  supportTrackerTotalDone + supportTrackerTotalPending
      }

      dataForSupportTracker.totalConfirmations = supportTrackerTotal
      dataForSupportTracker.totalDone = supportTrackerTotalDone
      dataForSupportTracker.totalPending = supportTrackerTotalPending
      dataForSupportTracker.totalMeta = supportTrackerTotalMeta

      dataForCharts.value = dataForSupportTracker

      workersInside.sort(function (a, b) {
        return b.confirmationsCount - a.confirmationsCount
      })

      supervisors.value = workersInside.map(worker => ({
        supervisor: worker.name,
        observed: worker.confirmationsCount,
      }))
    }

    // Adherence
    async function listConfirmations() {
      isLoadingConfirmations.value = true
      const [startFilter, endFilter] = getDatesFromRange(dateRangeFilter.value)

      try {
        const initialQuery = {
          client_id: oid(clientId),
          date: { $gte: date(startFilter), $lt: date(endFilter) },
          pending: { $ne: true }
        }

        const finalQuery = {}

        if (locationFilter.value.length > 0) {
          finalQuery['worker.locations._id'] = { $in: locationFilter.value }
        }

        if (physicalLocationFilter.value.length > 0) {
          finalQuery['worker.physical_locations._id'] = { $in: physicalLocationFilter.value }
        }

        const pipeline = [
          { $match: initialQuery },
          {
            $lookup: {
              from: 'worker',
              localField: 'worker',
              foreignField: '_id',
              pipeline: [
                { $project: { name: 1, locations: 1, physical_locations: 1, roles: 1 } },
                {
                  $lookup: {
                    from: 'location',
                    localField: 'locations',
                    foreignField: '_id',
                    pipeline: [ { $project: { location: 1, zone: 1, agency: 1 } }, { $addFields: { _id: { $toString: "$_id" } } } ],
                    as: 'locations',
                  },
                },
                {
                  $lookup: {
                    from: 'physical_location',
                    localField: 'physical_locations',
                    foreignField: '_id',
                    pipeline: [ { $project: { name: 1 } }, { $addFields: { _id: { $toString: "$_id" } } }],
                    as: 'physical_locations'
                  }
                },
                { $addFields: { _id: { $toString: "$_id" } } }
              ],
              as: 'worker'
            }
          },
          { $match: finalQuery },
          {
            $lookup: {
              from: 'worker',
              localField: 'supervisor',
              foreignField: '_id',
              pipeline: [ { $project: { name: 1 } }, { $addFields: { _id: { $toString: "$_id" } } } ],
              as: 'supervisor'
            }
          },
          {
            $lookup: {
              from: 'process',
              localField: 'process',
              foreignField: '_id',
              pipeline: [ { $project: { name: 1 } }, { $addFields: { _id: { $toString: "$_id" } } } ],
              as: 'process'
            }
          },
          { $addFields: { _id: { $toString: "$_id" }, worker: { $arrayElemAt: ["$worker", 0] }, supervisor: { $arrayElemAt: ["$supervisor", 0] }, process: { $arrayElemAt: ["$process", 0] } } },
        ]
      
        const items = await getItemsAggregate({ collection: 'confirmation', pipeline })

        parseConfirmationData(items)
      } catch (error) {
        console.log(error)
        handleError({ error, defaultMessage: i18n.t('message.err_confirmation_list') })
      } finally {
        isLoadingConfirmations.value = false
      }
    }

    function parseConfirmationData(items) {
      let workerData = {}

      for (const confirmation of items) {
        if (confirmation.total) {
          const workerName = confirmation.worker?.name
          const workerId = confirmation.worker?._id
          const score = confirmation.score
          const total = confirmation.total
          
          // Data for bar charts
          if (workerName) updateObjectData(workerData, workerName, workerId, score, total)
        }
      }
      
      // Set charts data
      workerBottomAdherence.value = setChartsData(workerData, "asc", 10)
    }

    function updateFilter(data) {
      locationFilter.value = data.locationFilter
      physicalLocationFilter.value = data.physicalLocationFilter
      dateRangeFilter.value = data.dateRangeFilter

      listMeetings()
      listConfirmations()

      if (compliance_calculation_by_role) listWorkers()
      else listEvents()
    }

    function updateLocations(data) {
      if (!locations.value.length) {
        locations.value = data;
        listMeetings();
      }
    }

    const updateObjectData = (object, property, propertyId, score, total) => {
      if (object.hasOwnProperty(property)) {
        object[property].score += score
        object[property].total += total
        object[property].confirmations += 1
      }
      else object[property] = {
        score: score,
        total: total,
        confirmations: 1,
        id: propertyId
      }
    }

    const setChartsData = (objectData, sortDirection, dataLimit) => {
      for (const property in objectData) {
        objectData[property].adherence = processAdherence(objectData[property].score, objectData[property].total)
      }

      let dataSorted = Object.entries(objectData)

      dataSorted.sort(function (a, b) {
        if (b[1].score === 0 && a[1].score === 0) return sortDirection === "desc" ? a[1].total - b[1].total : b[1].total - a[1].total
        return sortDirection === "desc" ? b[1].adherence - a[1].adherence : a[1].adherence - b[1].adherence
      })

      const limit = dataLimit < dataSorted.length ? dataLimit : dataSorted.length

      let labels = []
      let adherenceValues = []
      let confirmationValues = []
      let idValues = []

      for (let i = 0; i < limit; i++) {
        labels.push(dataSorted[i][0])
        adherenceValues.push(dataSorted[i][1].adherence)
        confirmationValues.push(dataSorted[i][1].confirmations)
        idValues.push(dataSorted[i][1].id)
      }

      return {
        labels: labels,
        data: {
          adherence: adherenceValues,
          confirmations: confirmationValues
        },
        additionalInfo: {
          ids: idValues,
        }
      }
    }

    // Capture and send report
    const getDepartmentsData = async () => {
      try {
        const query = {
          _id: { $in: locationFilter.value.map(e => oid(e)) },
        }

        const pipeline = [
          { $match: query },
          {
            $lookup: {
              from: 'area',
              localField: 'area',
              foreignField: '_id',
              pipeline: [
                {
                  $lookup: {
                    from: 'worker',
                    localField: 'areaLeads',
                    foreignField: '_id',
                    pipeline: [
                      { $project: { name: 1, email: 1 } },
                      { $addFields: { _id: { $toString: "$_id" } } }
                    ],
                    as: 'areaLeads'
                  }
                },
                {
                  $project: {
                    name: 1,
                    areaLeads: 1
                  }
                },
                { $addFields: { _id: { $toString: "$_id" } } }
              ],
              as: 'area'
            }
          },
          { $addFields: { _id: { $toString: "$_id" }, area: { $arrayElemAt: ["$area", 0] } } },
        ]
      
        const items = await getItemsAggregate({ collection: 'location', pipeline })
        return items
      } catch (error) {
        console.log(error);
        handleError({ error, defaultMessage: i18n.t('message.location_fetch_error') })
      }
    }

    const captureReportImage = async () => {
      const reportElement = document.querySelector('#report')
      if (!reportElement) return null

      const canvas = await html2canvas(reportElement, { backgroundColor: '#f5f5f5' })
      const dataUrl = canvas.toDataURL('image/png')
      return dataUrl
    }

    const sendReportEmail = async () => {
      try {
        if (!locationFilter.value.length) {
          handleError({ defaultMessage: i18n.t('message.mustSelectOneDepartment') })
          return
        }

        sendingReportMessage.value = i18n.t('message.sendingReport')

        const departmentsData = await getDepartmentsData()
        let physicalLocationsNames = ''
        if (physicalLocationFilter.value.length && physical_locations.value.length) {
          const selectedNames = physical_locations.value
            .filter(loc => physicalLocationFilter.value.includes(loc.value))
            .map(loc => loc.title)
          physicalLocationsNames = selectedNames.length ? selectedNames.join(' / ') : ''
        }

        if (!departmentsData?.length) {
          handleError({ defaultMessage: i18n.t('message.noDepartmentDataFound') })
          return
        }

        const areaLeads = departmentsData.reduce((acc, location) => {
          if (location.area?.areaLeads?.length) {
            location.area.areaLeads.forEach(lead => {
              if (lead.email && !acc.includes(lead.email)) {
                acc.push(lead.email)
              }
            })
          }
          return acc
        }, [])

        if (!areaLeads.length) {
          handleError({ defaultMessage: i18n.t('message.noAreaLeadsFound') })
          return
        }

        const imageDataUrl = await captureReportImage()
        if (!imageDataUrl) return

        const base64Image = imageDataUrl.replace(/^data:image\/png;base64,/, "")

        // La Lambda arma el MIME con { filename, data, type }: los campos en mayúsculas eran
        // los del RawMessage que armaba este componente con la SES directa.
        const attachment = {
          filename: `${i18n.t('Department Report')}.png`,
          data: base64Image,
          type: 'image/png',
        }

        const departments = departmentsData.map(e => e.location).join(' / ')
        const subject = `${i18n.t('Department Report')}: ${departments}`
        const body = getBodyTemplate(departments, physicalLocationsNames)

        await sendEmail({ to: areaLeads, subject, htmlBody: body, attachment })
        showSuccessMessage(i18n.tc('message.numberOfEmailsSent', 0, { count: areaLeads.length }))
      } catch (error) {
        console.log(error)
        handleError({ error, defaultMessage: i18n.t('message.errorSendingEmail') })
      } finally {
        sendingReportMessage.value = ''
      }
    }

    const getBodyTemplate = (departments, physicalLocationsNames) => {
      return `
        <html>
          <table style="max-width: 600px; padding: 10px; margin:0 auto; border-collapse: collapse; border-radius: 1em; overflow: hidden;">
            <tr>
              <td style="background-color: #ecf0f1; text-align: center; padding: 0">
                <img width="20%" style="margin: 2% 0% 1%" src="${LOGO_CORREO}" alt="Habit Logo">
              </td>
            </tr>
            
            <tr>
              <td style="background-color: #ecf0f1">
                <div style="color: #34495e; margin: 2% 10%; font-family: sans-serif">
                  <p style="margin: 2px; font-size: 15px">
                    ${i18n.t('message.attachedIsTheDepartmentReport')}: ${departments}
                  </p>
                  ${ physicalLocationsNames.length ? `<p style="margin: 2px; font-size: 15px">
                    ${i18n.t('label.Physical Locations')}: ${physicalLocationsNames}
                  </p>` : ''}
                  <p style="margin: 2px; font-size: 15px">
                    ${i18n.t('message.pressButtonToSeeInHabit')}:
                  </p>

                  <div style="width: 100%; text-align: center; margin: 7% 0%;">
                    <a style="text-decoration: none; border-radius: 5px; padding: 11px 23px; color: white; background-color: #4e8769; font-size: 17px" href="${endpoints.app_url}/habit/department/report">
                      ${i18n.t('message.see_report_in_habit')}
                    </a>
                  </div>
                </div>
              </td>
            </tr>
          </table>
        </html>
      `
    }

    onMounted(() => {
      listMeetings()
      listConfirmations()
      getPhysicalLocationsForDropDown()

      if (compliance_calculation_by_role) listWorkers()
      else listEvents()
    })

    return {
      updateFilter,
      updateLocations,
      workerBottomAdherence,
      router,
      isLoadingMeetings,
      isLoadingCompliance,
      isLoadingConfirmations,
      colors,
      compliance,
      complianceColor,
      dialoguesPerDay,
      happiness_index,
      happinessPerDay,
      happinessHappy,
      happinessNeutral,
      happinessSad,
      supervisors,
      trackerData,
      dataForCharts,
      sendReportEmail,
      sendingReportMessage,
      selectedLocationsData,
      compliance_calculation_by_role,
      default_language,
    };
  },
};
</script>
